import { Box, Flex, useBreakpointValue, type BoxProps } from '@chakra-ui/react';
import { useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useMobileLargeTitleProgress } from '../../hooks/useMobileLargeTitleProgress';
import { useScrollAwayChrome } from '../../hooks/useScrollAwayChrome';
import { BackOrFallbackButton } from './BackOrFallbackButton';
import { extractNavTitle } from './extractNavTitle';
import { MOBILE_LARGE_TITLE_STAGE_CSS } from './mobileNavTitleStages';
import { usePageChrome, type PageHeaderBack } from './PageHeaderNavigationContext';
import { shouldCollapseMobilePageTitle } from './routeChrome';

export interface AppPageHeaderProps extends Omit<BoxProps, 'title'> {
  /** Primary page title — typically `<PageTitle>…</PageTitle>`. */
  title: ReactNode;
  /**
   * Plain string for the mobile navigation bar. Defaults to text extracted from
   * `title`. Use when the title tree is complex (controls, avatars, subcopy).
   */
  navTitle?: string;
  /** Optional page-level back/up affordance for detail routes. */
  back?: PageHeaderBack;
  /**
   * `above` — back control on its own row, title below.
   * `beside` — title shares the back control's row, so the page keeps that height.
   */
  backPlacement?: 'above' | 'beside';
  /** Stats, export actions, badges — beside the title on `md+`, stacked on narrow screens. */
  trailing?: ReactNode;
  /**
   * `stack` — title full width on mobile, trailing on the row below (default).
   * `inline` — title and trailing share one row at all breakpoints (compact actions).
   * `inline-wrap` — same row when it fits; otherwise the title stays intact and
   * trailing moves to the next line (small desktop beside a sidebar).
   */
  trailingLayout?: 'stack' | 'inline' | 'inline-wrap';
  /**
   * `start`: default tool layout (left-aligned with main column).
   * `center`: marketing / empty-state style (home, sign-in gates).
   */
  align?: 'start' | 'center';
  /**
   * When true (default on mobile for `start` align without large→compact),
   * the title band scrolls away like a secondary toolbar.
   * Auto-disabled when the large title crossfades into the nav bar.
   */
  scrollAway?: boolean;
  /**
   * Large in-page title tucks under the floating header pills on scroll, then
   * a compact centered title appears (WhatsApp / HIG large-title stages).
   * Defaults on for start-aligned headers.
   */
  collapseMobileTitle?: boolean;
  /**
   * Let clicks on the large title fall through to content painted behind it.
   * The mobile title track is transformed, which otherwise keeps the hit.
   */
  titlePointerEvents?: 'auto' | 'none';
}

const DEFAULT_MB = 6;
const SCROLL_AWAY_CSS_VAR = '--page-title-scroll-away';

/**
 * Shared page header band: consistent width, spacing after `PageTitle`, optional trailing actions.
 * Use on every route in `AppLayout` so titles share the same vertical rhythm as content below.
 */
export function AppPageHeader({
  title,
  navTitle,
  back,
  backPlacement = 'above',
  trailing,
  trailingLayout = 'stack',
  align = 'start',
  mb = DEFAULT_MB,
  scrollAway,
  collapseMobileTitle: collapseMobileTitleProp,
  titlePointerEvents,
  display,
  ...boxProps
}: AppPageHeaderProps) {
  const { pathname } = useLocation();
  const resolvedNavTitle =
    align === 'center' ? undefined : (navTitle?.trim() || extractNavTitle(title));
  usePageChrome({
    back,
    title: resolvedNavTitle,
  });

  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const collapseMobileTitle =
    collapseMobileTitleProp ??
    (align === 'start' && shouldCollapseMobilePageTitle(pathname));
  // Drive absolute scroll-range stages whenever the route opts in.
  const largeTitleStages = collapseMobileTitle;
  const scrollAwayEnabled =
    scrollAway ?? (isMobile && align === 'start' && !collapseMobileTitle);
  const resolvedMb =
    mb === DEFAULT_MB && collapseMobileTitle
      ? { base: 3, md: DEFAULT_MB }
      : mb;

  const titleTrackRef = useRef<HTMLDivElement | null>(null);
  const styleTargetRef = useRef<HTMLDivElement | null>(null);
  useMobileLargeTitleProgress(largeTitleStages, titleTrackRef);
  const { away } = useScrollAwayChrome(scrollAwayEnabled, {
    styleTargetRef,
    cssVariableName: SCROLL_AWAY_CSS_VAR,
    maxOffsetPx: 72,
  });

  if (align === 'center') {
    return (
      <Box w="100%" minW={0} mb={mb} textAlign="center" display={display} {...boxProps}>
        {back ? (
          <BackOrFallbackButton
            fallback={back.fallback}
            label={back.label}
            ariaLabel={back.ariaLabel}
            display={{ base: 'none', md: 'inline-flex' }}
            mb={2}
            mx="auto"
          />
        ) : null}
        {title}
        {trailing ? <Box mt={4}>{trailing}</Box> : null}
      </Box>
    );
  }

  const inline = trailingLayout === 'inline' || trailingLayout === 'inline-wrap';
  const wrapInline = trailingLayout === 'inline-wrap';

  return (
    <Box
      ref={styleTargetRef}
      w="100%"
      minW={0}
      mb={resolvedMb}
      display={display}
      {...boxProps}
      style={{
        ...(typeof boxProps.style === 'object' && boxProps.style !== null ? boxProps.style : null),
        ...(scrollAwayEnabled
          ? {
              transform: `translateY(calc(var(${SCROLL_AWAY_CSS_VAR}, 0) * -28px))`,
              opacity: `calc(1 - var(${SCROLL_AWAY_CSS_VAR}, 0) * 0.85)`,
              willChange: 'transform, opacity',
            }
          : null),
      }}
      aria-hidden={away || undefined}
      pointerEvents={away ? 'none' : boxProps.pointerEvents}
    >
      <Flex
        direction={inline ? 'row' : { base: 'column', md: 'row' }}
        align={inline ? 'center' : { base: 'stretch', md: 'flex-start' }}
        justify="space-between"
        flexWrap={wrapInline ? 'wrap' : 'nowrap'}
        columnGap={wrapInline ? { base: 3, md: 4 } : undefined}
        rowGap={wrapInline ? 3 : undefined}
        gap={wrapInline ? undefined : { base: 3, md: 4 }}
        w="100%"
      >
        {/* Large title: reverse of the compact nav title (blur out + lift) on the same stage. */}
        <Box
          ref={titleTrackRef}
          pointerEvents={titlePointerEvents}
          flex={wrapInline ? undefined : '1'}
          minW={wrapInline ? undefined : 0}
          css={{
            ...(wrapInline
              ? {
                  flex: '1 0 auto',
                  // Stay as wide as the title, unless the column itself is narrower.
                  minWidth: 'min(100%, max-content)',
                  maxWidth: '100%',
                }
              : null),
            ...(largeTitleStages ? MOBILE_LARGE_TITLE_STAGE_CSS : null),
          }}
        >
          {back && backPlacement === 'beside' ? (
            <Flex align="center" gap={1} minW={0}>
              <BackOrFallbackButton
                fallback={back.fallback}
                label={back.label}
                ariaLabel={back.ariaLabel}
                display={{ base: 'none', md: 'inline-flex' }}
                flexShrink={0}
              />
              <Box minW={0}>{title}</Box>
            </Flex>
          ) : (
            <>
              {back ? (
                <BackOrFallbackButton
                  fallback={back.fallback}
                  label={back.label}
                  ariaLabel={back.ariaLabel}
                  display={{ base: 'none', md: 'inline-flex' }}
                  mb={2}
                  alignSelf="flex-start"
                />
              ) : null}
              {title}
            </>
          )}
        </Box>
        {trailing ? (
          <Flex
            flex={inline ? undefined : { base: 'none', md: 1 }}
            minW={wrapInline ? undefined : 0}
            shrink={wrapInline ? undefined : 0}
            align={inline ? 'center' : 'flex-start'}
            justify={inline ? 'flex-end' : { base: 'flex-start', md: 'flex-end' }}
            gap={2}
            flexWrap="wrap"
            w={inline ? 'auto' : { base: '100%', md: 'auto' }}
            ml={wrapInline ? 'auto' : undefined}
            pt={inline ? { base: 0.5, md: 1 } : undefined}
            css={wrapInline
              ? {
                  flex: '1 1 auto',
                  // Wrap the whole cluster under the title before it crushes the heading.
                  minWidth: 'min(100%, max-content)',
                }
              : undefined}
          >
            {trailing}
          </Flex>
        ) : null}
      </Flex>
    </Box>
  );
}
